import { useQuery } from '@apollo/client';
import { GET_CHILDREN } from '@/graphql/queries';
import { GetChildrenResponse } from '@/graphql/types';
import { ApolloClientName } from '@/config/apolloClient';
import { grades as gradeOptions } from '@/pages/AccountInformation/AccountInformation.constants';
import { useMemo } from 'react';
import { getGradeRange } from '@/utils/getGradeRange';

export const useChildGrades = () => {
  const { data, loading, error, refetch } = useQuery<GetChildrenResponse>(GET_CHILDREN, {
    fetchPolicy: 'cache-first',
    context: {
      clientName: ApolloClientName.AccountsBff,
    },
  });

  const dataChildren = data?.profileNewModel?.children ?? [];

  const activeChildren = useMemo(
    () => dataChildren.filter(child => child.status === 'active'),
    [dataChildren]
  );

  const { lowGrade, highGrade, grades } = useMemo(() => {
    if (loading) {
      return { lowGrade: '', highGrade: '', grades: [] };
    }

    if (activeChildren.length === 0) {
      return { lowGrade: 'PK', highGrade: 'CE', grades: getGradeRange('PK', 'CE') };
    }

    const gradeBounds = activeChildren
      .map(child => child.institutionGradeLevel)
      .filter(Boolean)
      .map(level => level.split('-'))
      .filter(arr => arr.length === 2);

    if (gradeBounds.length === 0) {
      return { lowGrade: '', highGrade: '', grades: [] };
    }

    const allLows = gradeBounds.map(([low]) => low);
    const allHighs = gradeBounds.map(([, high]) => high);

    const gradeOrder = gradeOptions.map(g => g.id);

    const minIndex = Math.min(...allLows.map(g => gradeOrder.indexOf(g)).filter(i => i !== -1));
    const maxIndex = Math.max(...allHighs.map(g => gradeOrder.indexOf(g)).filter(i => i !== -1));

    if (minIndex === -1 || maxIndex === -1) {
      return { lowGrade: '', highGrade: '', grades: [] };
    }

    const lowGrade = gradeOrder[minIndex];
    const highGrade = gradeOrder[maxIndex];
    const grades = getGradeRange(lowGrade, highGrade);

    return { lowGrade, highGrade, grades };
  }, [activeChildren, loading]);

  return { loading, error, refetch, lowGrade, highGrade, grades };
};
